import { useCallback, useEffect, useMemo, useState } from "react";
import { Wrench, Plus, Trash2, Loader2, Mail, Clock, CheckCircle2, AlertCircle, ShieldX } from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { cn } from "@/lib/cn";
import LoadError from "@/components/dashboard/LoadError";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import Modal from "@/components/dashboard/Modal";
import { MAINTENANCE_CHANGED } from "@/components/dashboard/SessionBanners";
import {
  getMaintenance, updateMaintenance, listBlockedIps, blockIp, unblockIp,
  getLoginNotification, updateLoginNotification, getInactivityReminder, updateInactivityReminder,
} from "@/api/admin";

// IPv4 dotted-quad with 0-255 octets, or a plausible IPv6 (hex groups with an
// optional :: run). Mirrors what the edge blocker can actually match.
const IPV4 = /^(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)(\.(25[0-5]|2[0-4]\d|1\d\d|[1-9]?\d)){3}$/;
const IPV6 = /^(([0-9a-fA-F]{1,4}:){7}[0-9a-fA-F]{1,4}|([0-9a-fA-F]{1,4}:){1,7}:|:(:[0-9a-fA-F]{1,4}){1,7}|([0-9a-fA-F]{1,4}:){1,6}(:[0-9a-fA-F]{1,4}){1,1}|([0-9a-fA-F]{1,4}:){1,5}(:[0-9a-fA-F]{1,4}){1,2}|([0-9a-fA-F]{1,4}:){1,4}(:[0-9a-fA-F]{1,4}){1,3}|([0-9a-fA-F]{1,4}:){1,3}(:[0-9a-fA-F]{1,4}){1,4}|([0-9a-fA-F]{1,4}:){1,2}(:[0-9a-fA-F]{1,4}){1,5}|::)$/;
const isValidIp = (v) => IPV4.test(v) || IPV6.test(v);

function Card({ icon: Icon, title, subtitle, children }) {
  return (
    <div className="card p-6">
      <h3 className="flex items-center gap-2 text-base font-semibold text-slate-800 dark:text-white"><Icon size={17} className="text-brand-500" /> {title}</h3>
      {subtitle && <p className="mt-0.5 text-sm text-slate-400">{subtitle}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}
function Toggle({ checked, onChange }) {
  return <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} className="h-5 w-9 accent-brand-600" />;
}

export default function PlatformOps() {
  const [toast, setToast] = useState(null);
  const [maint, setMaint] = useState({ enabled: false, message: "" });
  // The last state the SERVER confirmed, so each card can show whether it has
  // unsaved changes instead of leaving the operator to guess.
  const [savedMaint, setSavedMaint] = useState({ enabled: false, message: "" });
  const [savedLogin, setSavedLogin] = useState(null);
  const [savedInact, setSavedInact] = useState(null);
  const [confirmMaint, setConfirmMaint] = useState(null);
  const [maintTyped, setMaintTyped] = useState("");
  const [yourIp, setYourIp] = useState(null);
  const [ipQuery, setIpQuery] = useState("");
  const [login, setLogin] = useState({ enabled: false, video_url: "", thumbnail_url: "" });
  const [inact, setInact] = useState({ enabled: false, days: 14, cooldown_days: 14 });
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState({ ip: "", reason: "" });
  const [pendingUnblock, setPendingUnblock] = useState(null);
  const [unblocking, setUnblocking] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Every one of these four GETs used to be swallowed by `catch (_) {}`, so a
  // failed load left the page showing its blank initial state — indistinguishable
  // from "maintenance is off, nothing is blocked, no reminders configured".
  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, l, i, b] = await Promise.all([getMaintenance(), getLoginNotification(), getInactivityReminder(), listBlockedIps()]);
      setMaint(m); setLogin(l); setInact(i); setIps(b.blocked_ips || []);
      setSavedMaint(m); setSavedLogin(l); setSavedInact(i);
      // The server is the only side that knows the operator's public address —
      // used to warn before they block themselves out at the edge.
      setYourIp(b.your_ip || null);
    } catch (e) {
      setError((e && e.message) || "Platform settings could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { loadAll(); }, [loadAll]);

  // Per-card "unsaved changes" state. The three cards used to behave differently
  // from each other with nothing on screen saying so.
  const same = (a, b) => JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  const maintDirty = !same(maint, savedMaint);
  const loginDirty = !same(login, savedLogin);
  const inactDirty = !same(inact, savedInact);
  const anyDirty = maintDirty || loginDirty || inactDirty;

  // The in-app dirty markers can't see a tab close or a reload.
  //
  // This has to sit BELOW `anyDirty`, not above it. A dependency array is
  // evaluated during render, at the useEffect call itself — so reading a `const`
  // declared further down hit its temporal dead zone and threw
  // "Cannot access 'anyDirty' before initialization" on every render, which took
  // the whole page down to a blank screen. (The callback body would have been
  // fine on its own; it doesn't run until after render.)
  useEffect(() => {
    if (!anyDirty) return undefined;
    const onBeforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [anyDirty]);

  const visibleIps = useMemo(() => {
    const q = ipQuery.trim().toLowerCase();
    if (!q) return ips;
    return ips.filter((b) => `${b.ip} ${b.reason || ""}`.toLowerCase().includes(q));
  }, [ips, ipQuery]);

  const ok = (m) => setToast({ type: "success", message: m });
  const err = (e) => setToast({ type: "error", message: (e && e.message) || "Failed." });

  // NOTHING on this page saves from a toggle. The maintenance switch used to PUT
  // immediately — one click 503'd every non-operator on the platform — while the
  // two switches below it only changed local state until their Save button was
  // pressed. Two interaction models on one page meant the other two silently
  // didn't save for anyone who assumed they behaved like the first.
  //
  // Turning maintenance ON additionally requires typing the word, because it is
  // the single most destructive control in the product.
  async function commitMaint(next) {
    const previous = savedMaint;
    setMaint(next);
    try {
      const res = await updateMaintenance(next);
      setSavedMaint(res || next);
      setConfirmMaint(null);
      setMaintTyped("");
      ok(next.enabled ? "Maintenance mode is ON — everyone else is getting a 503." : "Maintenance mode is off.");
      // Update the app-wide banner at once rather than on its next poll.
      window.dispatchEvent(new Event(MAINTENANCE_CHANGED));
    } catch (e) {
      setMaint(previous);
      err(e);
    }
  }

  function requestMaint(next) {
    // The 503 body is the ONLY thing users will see, for as long as this is on.
    if (next.enabled && !String(next.message || "").trim()) {
      return err({ message: "Write the message users will see before turning maintenance on." });
    }
    setMaintTyped("");
    setConfirmMaint(next);
  }
  async function saveLogin() {
    // https only: these URLs are embedded in an email sent to every user on
    // sign-in, and a plain-http asset is blocked by most clients and flagged by
    // some as phishing.
    for (const [field, label] of [["video_url", "Video URL"], ["thumbnail_url", "Thumbnail URL"]]) {
      const raw = String(login[field] || "").trim();
      if (!raw) continue;
      let parsed;
      try { parsed = new URL(raw); } catch (_) { return err({ message: `${label} isn't a valid URL.` }); }
      if (parsed.protocol !== "https:") return err({ message: `${label} must start with https://.` });
    }
    try {
      const res = await updateLoginNotification(login);
      setSavedLogin(res || login);
      ok("Login email settings saved.");
    } catch (e) { err(e); }
  }
  async function saveInact() {
    const days = Number(inact.days);
    const cooldown = Number(inact.cooldown_days);
    // An emptied field arrives as 0, which would mean "remind everyone who has
    // been idle for no time at all" — i.e. mail the entire user base.
    if (!Number.isInteger(days) || days < 1) return err({ message: "Inactivity days must be a whole number of 1 or more." });
    if (!Number.isInteger(cooldown) || cooldown < 1) return err({ message: "Cooldown days must be a whole number of 1 or more." });
    try {
      const res = await updateInactivityReminder({ ...inact, days, cooldown_days: cooldown });
      setSavedInact(res || inact);
      ok("Inactivity reminder saved.");
    } catch (e) { err(e); }
  }

  async function addIp() {
    const ip = newIp.ip.trim();
    // The server refuses this too (it is the only side that reliably knows the
    // caller's address); saying so here avoids a pointless round-trip.
    if (yourIp && ip === yourIp) {
      return err({ message: "That's the address you're connected from — blocking it would lock you out." });
    }
    // Only "not empty" was checked, so any string was accepted and stored as a
    // block rule that could never match a real client address.
    if (!isValidIp(ip)) return err({ message: "Enter a valid IPv4 or IPv6 address." });
    if (ips.some((b) => b.ip === ip)) return err({ message: "That IP is already blocked." });
    try { await blockIp({ ...newIp, ip }); setNewIp({ ip: "", reason: "" }); await loadAll(); ok("IP blocked."); } catch (e) { err(e); }
  }
  // The trash icon used to fire the DELETE straight away, so one mis-aimed click
  // in a dense list unblocked an address at the edge with no prompt and no undo.
  async function removeIp() {
    if (!pendingUnblock || unblocking) return;
    setUnblocking(true);
    try {
      await unblockIp(pendingUnblock.id);
      setPendingUnblock(null);
      await loadAll();
      ok("IP unblocked.");
    } catch (e) { err(e); } finally { setUnblocking(false); }
  }

  return (
    <>
      <PageHeader title="Platform Ops" subtitle="Maintenance mode, IP blocking, and notification settings." />
      {toast && <div className={cn("mb-5 flex items-center gap-2 rounded-xl border px-4 py-2.5 text-sm", toast.type === "error" ? "border-rose-200 bg-rose-50 text-rose-700 dark:border-rose-500/20 dark:bg-rose-500/10" : "border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/20 dark:bg-emerald-500/10")}>{toast.type === "error" ? <AlertCircle size={15} /> : <CheckCircle2 size={15} />}<span className="flex-1">{toast.message}</span><button onClick={() => setToast(null)}>✕</button></div>}

      <LoadError message={error} onRetry={loadAll} />

      {loading ? <div className="flex justify-center py-16 text-slate-400"><Loader2 size={24} className="animate-spin" /></div> : error ? null : (
        <div className="grid gap-5 lg:grid-cols-2">
          <Card icon={Wrench} title="Maintenance mode" subtitle="When on, non-admins get a 503 with your message.">
            <label className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 dark:border-white/10">
              <span className={cn("text-sm font-medium", maint.enabled ? "text-rose-600 dark:text-rose-300" : "text-slate-700 dark:text-slate-200")}>{maint.enabled ? "Maintenance is ON" : "Live"}</span>
              <Toggle checked={maint.enabled} onChange={(v) => requestMaint({ ...maint, enabled: v })} />
            </label>
            <textarea
              rows={2}
              value={maint.message || ""}
              onChange={(e) => setMaint((m) => ({ ...m, message: e.target.value }))}
              maxLength={500}
              placeholder="We'll be back shortly…"
              aria-label="Maintenance message shown to users"
              className="mt-3 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
            />
            <p className="mt-1 text-xs text-slate-400">
              This is the only thing users will see while maintenance is on.
            </p>
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" onClick={() => requestMaint(maint)} disabled={!maintDirty}>
                {maintDirty ? "Save maintenance settings" : "Saved"}
              </Button>
              {maintDirty && <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Unsaved changes</span>}
            </div>
          </Card>

          <Card icon={ShieldX} title="IP blocker" subtitle="Blocked IPs are rejected at the edge.">
            <div className="flex gap-2">
              <Input placeholder="203.0.113.9" value={newIp.ip} onChange={(e) => setNewIp((s) => ({ ...s, ip: e.target.value }))} />
              <Input placeholder="reason" value={newIp.reason} onChange={(e) => setNewIp((s) => ({ ...s, reason: e.target.value }))} />
              <Button size="sm" onClick={addIp}><Plus size={15} /></Button>
            </div>
            {yourIp && (
              <p className="mt-2 text-xs text-slate-400">
                You&apos;re connecting from <span className="font-mono font-medium text-slate-500 dark:text-slate-300">{yourIp}</span> — blocking it is refused.
              </p>
            )}
            {/* Search + a capped list. A long blocklist was an unbounded,
                unsearchable stack of rows. */}
            {ips.length > 8 && (
              <input
                type="search"
                value={ipQuery}
                onChange={(e) => setIpQuery(e.target.value)}
                placeholder="Search blocked IPs…"
                aria-label="Search blocked IPs"
                className="mt-3 h-9 w-full rounded-lg border border-slate-200 bg-white px-3 text-sm dark:border-white/10 dark:bg-white/5 dark:text-slate-200"
              />
            )}
            <div className="mt-3 max-h-72 space-y-1.5 overflow-y-auto">
              {ips.length === 0 ? <p className="text-xs text-slate-400">No IPs blocked.</p>
               : visibleIps.length === 0 ? <p className="text-xs text-slate-400">No blocked IP matches that search.</p>
               : visibleIps.map((b) => (
                <div key={b.id} className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-1.5 text-sm dark:border-white/10">
                  <span className="font-mono text-slate-700 dark:text-slate-200">{b.ip}</span>
                  <div className="flex items-center gap-2"><span className="text-xs text-slate-400">{b.reason}</span><button onClick={() => setPendingUnblock(b)} className="text-slate-400 hover:text-rose-500"><Trash2 size={14} /></button></div>
                </div>
              ))}
            </div>
          </Card>

          <Card icon={Mail} title="New-login email" subtitle="Email users on sign-in, with an optional welcome video.">
            <label className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 dark:border-white/10">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Send login notification</span>
              <Toggle checked={login.enabled} onChange={(v) => setLogin((s) => ({ ...s, enabled: v }))} />
            </label>
            <Input className="mt-3" label="Video URL" placeholder="https://youtu.be/…" value={login.video_url || ""} onChange={(e) => setLogin((s) => ({ ...s, video_url: e.target.value }))} />
            <Input label="Thumbnail URL (optional)" value={login.thumbnail_url || ""} onChange={(e) => setLogin((s) => ({ ...s, thumbnail_url: e.target.value }))} />
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" onClick={saveLogin} disabled={!loginDirty}>{loginDirty ? "Save" : "Saved"}</Button>
              {loginDirty && <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Unsaved changes</span>}
            </div>
          </Card>

          <Card icon={Clock} title="Inactivity reminder" subtitle="Email users who've gone quiet.">
            <label className="flex items-center justify-between rounded-xl border border-slate-200 px-4 py-3 dark:border-white/10">
              <span className="text-sm font-medium text-slate-700 dark:text-slate-200">Send reminders</span>
              <Toggle checked={inact.enabled} onChange={(v) => setInact((s) => ({ ...s, enabled: v }))} />
            </label>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <Input label="Inactive after (days)" type="number" value={inact.days} onChange={(e) => setInact((s) => ({ ...s, days: Number(e.target.value) }))} />
              <Input label="Cooldown (days)" type="number" value={inact.cooldown_days} onChange={(e) => setInact((s) => ({ ...s, cooldown_days: Number(e.target.value) }))} />
            </div>
            <div className="mt-3 flex items-center gap-2">
              <Button size="sm" onClick={saveInact} disabled={!inactDirty}>{inactDirty ? "Save" : "Saved"}</Button>
              {inactDirty && <span className="text-xs font-medium text-amber-600 dark:text-amber-400">Unsaved changes</span>}
            </div>
          </Card>
        </div>
      )}

      {/* Turning maintenance ON needs the word typed. It 503s every non-operator
          on the platform instantly, and it was previously a single click on a
          switch — the least guarded control in the product for the most
          destructive effect. Turning it OFF is a plain confirm: the risk runs
          one way. */}
      <Modal
        open={!!confirmMaint}
        onClose={() => { setConfirmMaint(null); setMaintTyped(""); }}
        title={confirmMaint?.enabled ? "Turn maintenance mode ON?" : "Turn maintenance mode off?"}
        size="md"
        footer={
          <div className="flex justify-end gap-2">
            <Button variant="outline" size="sm" onClick={() => { setConfirmMaint(null); setMaintTyped(""); }}>
              Cancel
            </Button>
            <Button
              size="sm"
              variant={confirmMaint?.enabled ? "danger" : "primary"}
              onClick={() => commitMaint(confirmMaint)}
              disabled={confirmMaint?.enabled && maintTyped.trim().toUpperCase() !== "MAINTENANCE"}
            >
              {confirmMaint?.enabled ? "Turn maintenance ON" : "Go live"}
            </Button>
          </div>
        }
      >
        {confirmMaint?.enabled ? (
          <div className="space-y-3">
            <div className="flex items-start gap-3 rounded-xl border border-rose-200 bg-rose-50/70 p-4 text-sm text-rose-800 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-200">
              <AlertCircle size={18} className="mt-0.5 shrink-0" />
              <p>
                Every tester, developer, PM and company admin will get a <strong>503</strong> on their
                next request — including anyone mid-test-run. Only platform operators can still sign in.
              </p>
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">They will see</p>
              <p className="mt-1 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-700 dark:bg-white/5 dark:text-slate-200">
                {confirmMaint.message}
              </p>
            </div>
            <label className="block text-sm">
              <span className="text-slate-600 dark:text-slate-300">
                Type <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-rose-600 dark:bg-white/10">MAINTENANCE</code> to confirm
              </span>
              <input
                autoFocus
                value={maintTyped}
                onChange={(e) => setMaintTyped(e.target.value)}
                aria-label="Type MAINTENANCE to confirm"
                className="mt-1.5 w-full rounded-xl border border-slate-200 bg-slate-50 px-3 py-2.5 text-sm focus:border-rose-400 focus:bg-white focus:outline-none dark:border-white/10 dark:bg-white/5 dark:text-white"
              />
            </label>
          </div>
        ) : (
          <p className="text-sm text-slate-600 dark:text-slate-300">
            The platform goes back to normal immediately and everyone can sign in again.
          </p>
        )}
      </Modal>

      <ConfirmDialog
        open={pendingUnblock}
        title={pendingUnblock ? `Unblock ${pendingUnblock.ip}?` : ""}
        body="This address is rejected at the edge right now. Unblocking lets it reach the platform again on its next request, and the block cannot be restored without re-adding it."
        confirmLabel="Unblock"
        busy={unblocking}
        onConfirm={removeIp}
        onCancel={() => setPendingUnblock(null)}
      />
    </>
  );
}
